import type { ReactElement } from "react";
import type { RepairTier, SlipMeta } from "../_stubData.ts";
import { fmtMoney, fmtOdds } from "./SrIcon.tsx";

interface Props {
  meta: SlipMeta;
  tier: RepairTier;
  setTier: (t: RepairTier) => void;
  finalOdds: number;
  finalProb: number;
  finalPayout: number;
  replaced: number;
  retained: number;
  flagged: number;
  unresolved: number;
}

const TIER_META: Record<RepairTier, { label: string; cssVar: string; blurb: string }> = {
  bold:         { label: "Bold",         cssVar: "var(--sr-sharp)", blurb: "Preserves the dream — keeps most of the original payout." },
  moderate:     { label: "Moderate",     cssVar: "var(--sr-sharp)", blurb: "Trades some upside for picks we have higher confidence in." },
  safer:        { label: "Safer",        cssVar: "var(--sr-safer)", blurb: "Prioritises landing — leans on higher-probability picks." },
  conservative: { label: "Conservative", cssVar: "var(--sr-safer)", blurb: "Smallest payout, highest landing confidence." },
};

const TIER_ORDER: RepairTier[] = ["bold", "moderate", "safer", "conservative"];

export function RepairHero({
  meta,
  tier,
  setTier,
  finalOdds,
  finalProb,
  finalPayout,
  replaced,
  retained,
  flagged,
  unresolved,
}: Props): ReactElement {
  const tierMeta = TIER_META[tier];
  const tierColor = tierMeta.cssVar;
  return (
    <div className="sr-hero">
      <div className="sr-hero-head">
        <span className="sr-bookie-tag">
          <span className="b-mark">{meta.bookmakerShort}</span>
          {meta.bookmaker}
        </span>
        <div className="sr-hero-meta">
          <span>
            <span className="strong">{meta.legCount}</span> legs
          </span>
          <span className="sep">·</span>
          <span>
            Stake{" "}
            <span className="strong">
              {meta.currency}
              {fmtMoney(meta.stake)}
            </span>
          </span>
          <span className="sep">·</span>
          <span>
            Slip <span className="strong">#{meta.slipCode}</span>
          </span>
        </div>
        <div className="sr-hero-mode">
          {TIER_ORDER.map((t) => {
            const m = TIER_META[t];
            const sideClass = t === "bold" || t === "moderate" ? "sharp" : "safer";
            return (
              <button
                key={t}
                className={tier === t ? `active ${sideClass}` : ""}
                onClick={() => setTier(t)}
                title={m.blurb}
              >
                <span className="pip" />
                {m.label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="sr-transform">
        <div className="sr-transform-col original">
          <div className="sr-col-kicker">
            <span className="swatch" />
            Original slip
          </div>
          <div className="sr-odds-row">
            <span className="sr-odds-big">{fmtOdds(meta.origOdds)}</span>
            <span className="sr-odds-unit">×</span>
            <span className="sr-odds-delta neg">
              −{(((meta.origOdds - finalOdds) / meta.origOdds) * 100).toFixed(0)}%
            </span>
          </div>
          <div className="sr-payout-row">
            Potential payout{" "}
            <span className="v">
              {meta.currency}
              {fmtMoney(meta.origPayout)}
            </span>
          </div>
          <div className="sr-prob-row">
            <div className="sr-prob-track">
              <div className="fill" style={{ width: `${meta.origWinProb * 10}%` }} />
            </div>
            <span className="sr-prob-num">{meta.origWinProb.toFixed(2)}%</span>
          </div>
          <div style={{ marginTop: 8, fontSize: 11.5, color: "var(--text-tertiary)" }}>
            Win probability — your original combined chance to land.
          </div>
        </div>

        <div className="sr-transform-arrow">→</div>

        <div className="sr-transform-col repaired">
          <div className={`sr-col-kicker ${tier === "bold" || tier === "moderate" ? "sharp" : "safer"}`}>
            <span className="swatch" />
            {tierMeta.label} tier · repaired
          </div>
          <div className="sr-odds-row">
            <span className="sr-odds-big" style={{ color: tierColor }}>
              {fmtOdds(finalOdds)}
            </span>
            <span className="sr-odds-unit">×</span>
            <span className="sr-odds-delta sharp">
              {(finalProb / meta.origWinProb).toFixed(1)}× win prob
            </span>
          </div>
          <div className="sr-payout-row">
            Potential payout{" "}
            <span className="v">
              {meta.currency}
              {fmtMoney(finalPayout)}
            </span>
          </div>
          <div className="sr-prob-row">
            <div className="sr-prob-track">
              <div
                className={`fill ${tier === "bold" || tier === "moderate" ? "sharp" : "safer"}`}
                style={{ width: `${Math.min(100, finalProb * 8)}%` }}
              />
            </div>
            <span className="sr-prob-num">{finalProb.toFixed(2)}%</span>
          </div>
          <div style={{ marginTop: 8, fontSize: 11.5, color: "var(--text-tertiary)" }}>
            <span style={{ color: tierColor }}>{tierMeta.label}</span> — {tierMeta.blurb}
          </div>
        </div>
      </div>

      <div className="sr-hero-counts">
        <div className="sr-count retained">
          <span className="dot" />
          <span className="lab">Retained</span>
          <span className="num">{retained}</span>
        </div>
        <div className="sr-count replaced">
          <span className="dot" />
          <span className="lab">Replaced</span>
          <span className="num">{replaced}</span>
        </div>
        {unresolved > 0 && (
          <div className="sr-count" style={{ borderLeftColor: "var(--info)" }}>
            <span className="dot" style={{ background: "var(--info)" }} />
            <span className="lab">Unresolved</span>
            <span className="num">{unresolved}</span>
          </div>
        )}
        <div className="sr-count flagged">
          <span className="dot" />
          <span className="lab">Flagged for review</span>
          <span className="num">{flagged}</span>
        </div>
        <div className="sr-count">
          <span className="dot" style={{ background: "var(--accent)" }} />
          <span className="lab">Markets scanned</span>
          <span className="num">{meta.marketsScanned.toLocaleString()}</span>
        </div>
      </div>
    </div>
  );
}
